'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Fragment, memo, useMemo } from 'react';
import ViewportImage from '../../image/viewport-image';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { DeferredCardCartButton } from './deferred-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';

import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import { isHighValueProductPrice } from '../../../utils/high-value-product';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

export const CATEGORY_GRID_PRODUCT_IMAGE_SIZES =
  '(min-width: 1288px) 287px, (min-width: 1280px) calc((100vw - 140px) / 4), (min-width: 1024px) calc((100vw - 128px) / 4), (min-width: 768px) calc((100vw - 106px) / 3), (min-width: 484px) calc((100vw - 84px) / 2), 200px';

export type ProductCardProps = {
  product: Product;
  deferImage?: boolean;
  imageSizes?: string;
  className?: {
    main?: string;
  };
};

export const ProductCard = memo(function ProductCard({ product, className, deferImage, imageSizes }: ProductCardProps) {
  const CardImage = deferImage ? ViewportImage : Image;
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const attributes = useCategoryStore(state => state.filter.attributes);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const { t } = useTranslations();

  const image = useMemo(() => getProductImage(
    templateSettings,
    product,
    getProductFilterVariants(product, { attributes } as Parameters<typeof getProductFilterVariants>[1]),
  ), [product, attributes, templateSettings]);
  // Cards do not select variants. The variant dialog owns its own independent store.
  const productUrl = useMemo(() => getProductUrl(product, []), [product]);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });
  const highValueProduct =
    !priceDisplay.priceOnRequest && isHighValueProductPrice(priceDisplay.currentWithVat, currency);

  return (
    <div
      className={cn(
        'ea-product-card',
        'col-span-1 flex w-full flex-col overflow-hidden rounded-[16px] border border-black/5 p-[6px]',
        'group',
        className?.main,
      )}
    >
      <Link
        prefetchOnHover
        href={productUrl}
        className="relative aspect-square min-h-[200px] w-full min-w-[200px] overflow-hidden rounded-[8px]"
      >
        <CardImage
          src={image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(image, product?.name)}
          width={0}
          height={0}
          className="absolute inset-0 h-full w-full object-cover object-center"
          sizes={
            imageSizes ??
            '(min-width: 1280px) 310px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
          }
        />
        <div className="absolute left-2 top-2 flex flex-wrap items-center justify-start gap-1">
          {priceDisplay.showPreviousPrice ? (
            <Badge
              size="md"
              className={cn('ea-product-card__badge--discount', 'bg-[#c61e1eff] text-white')}
            >
              -{priceDisplay.discountLabel}%
            </Badge>
          ) : null}
          {product.tags
            ?.slice()
            ?.sort((a, b) => b.name.length - a.name.length)
            .map((badge, i) => (
              <Fragment key={`badge-${i}`}>
                <Badge
                  style={{
                    backgroundColor: badge.color ?? undefined,
                    color: getContrastYIQ(badge.color ?? '#ffffff'),
                  }}
                  size="md"
                  className={cn('ea-product-card__badge--tag')}
                >
                  {badge.name}
                </Badge>
              </Fragment>
            ))}
          {product?.quantity > 0 && product?.availability.id === 1 && (
            <Badge
              size="md"
              className={cn('ea-product-card__badge--availability')}
              style={{
                backgroundColor: product?.availability?.color ?? '#4CAF50',
                color: getContrastYIQ(product?.availability?.color ?? '#4CAF50'),
              }}
            >
              {product?.availability.name}
            </Badge>
          )}
        </div>
      </Link>
      <div className="relative z-10 flex max-h-20 items-end justify-end bg-white sm:max-h-[84px] md:max-h-[96px]">
        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2 rounded-t-[16px] bg-white p-1.5 sm:p-2 lg:p-3 xl:p-4',
            'ea-product-card__movable-content',
          )}
        >
          <p
            data-ea-visual-tag="h4"
            className="line-clamp-2 h-10 text-center text-sm font-medium text-black md:h-12 md:text-base"
          >
            {product?.name}
          </p>
          {priceDisplay.priceOnRequest ? (
            <strong className="text-center text-sm font-bold text-primary md:text-base">
              {t('text2278')}
            </strong>
          ) : (
            <div className="flex flex-col items-center justify-center">
              {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
                <span className="text-xs text-black/60">
                  {t('text1926')} {formatPrice(priceDisplay.mocPrice, currency, currencySettings)}
                </span>
              ) : null}
              <div className="flex items-baseline justify-center gap-2">
                <strong className="text-center text-sm font-bold md:text-base">
                  {formatPrice(priceDisplay.current, currency, currencySettings)}
                </strong>
                {priceDisplay.showPreviousPrice ? (
                  <span className="text-xs font-light text-black/70 line-through md:text-sm">
                    {formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}
                  </span>
                ) : null}
              </div>
              {templateSettings.product_teaser?.price_without_vat?.enabled ? (
                <span className="text-xs text-black/60">
                  {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                  {formatPrice(priceDisplay.secondary, currency, currencySettings)}{' '}
                  {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                </span>
              ) : null}
            </div>
          )}
          <div
            className={cn(
              'flex max-h-0 w-full items-center justify-between gap-2 overflow-hidden',
              'transition-all duration-300 ease-in-out group-focus-within:max-h-[60px] group-hover:max-h-[60px]',
              'ea-product-card__clickable-container',
            )}
          >
            {highValueProduct || priceDisplay.priceOnRequest ? (
              <Button
                asChild
                className={cn(
                  'h-11 w-[calc(100%-44px)] transition-all duration-150 ease-in-out sm:h-12 lg:h-[50px]',
                  'ea-product-card__detail-button',
                )}
              >
                <Link href={productUrl} prefetchOnHover aria-label={product.name ? `${t('text533')}: ${product.name}` : undefined}>
                  {t('text533')}
                </Link>
              </Button>
            ) : (
              <DeferredCardCartButton
                product={product}
                sellable={isSellable(product, 1)}
                className={cn(
                  'h-11 w-[calc(100%-44px)] transition-all duration-150 ease-in-out sm:h-12 lg:h-[50px]',
                  'ea-product-card__add-to-cart-button',
                )}
              />
            )}
            <AddToFavoritesButton
              slug={product.slug}
              isFavorite={product.is_favorite}
              productId={product.id}
              showText={false}
              className={{ button: cn('transition-all duration-150 ease-in-out') }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
